import { DbClient } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { createRouter, type ParsedLocation } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routerWithDbClient } from "@tanstack/react-router-with-db";
import type { SearchTerminalContext } from "@wags/discovery/index.ts";
import { SENTRY_DSN } from "@wags/telemetry/sentry-config.ts";

import { watchSessionViewer } from "@/lib/core/auth-session-client";
import { shouldPreloadOnIntent } from "@/lib/core/intent-preload";
import {
    installDeploySkewRecovery,
    recoverFromDeploySkew,
} from "@/lib/core/deploy-skew-recovery";
import { capturePageview } from "@/lib/core/posthog-pageview";
import { STREAMED_QUERY_RETRY } from "@/lib/core/query-retry";
import { createClientBeforeSend } from "@/lib/core/sentry-noise-filter";
import {
    rememberMatchSearchFromLocation,
    setMatchSearchViewer,
} from "@/lib/ui/last-match-search";
import { scrubContactTokens } from "../contact-token-scrub.mjs";
import { routeTree } from "./routeTree.gen";

function getScrollRestorationKey(location: ParsedLocation) {
    const defaultKey = location.state.__TSR_key ?? location.href;

    return location.pathname === "/discover"
        ? `discover:${defaultKey}`
        : defaultKey;
}

const analyticsEnabled =
    import.meta.env.PROD ||
    import.meta.env.VITE_PUBLIC_ENABLE_ANALYTICS === "true";

function bootSentry() {
    window.setTimeout(() => {
        void import("@sentry/tanstackstart-react").then((Sentry) => {
            Sentry.init({
                dsn: SENTRY_DSN,
                environment: import.meta.env.VITE_PUBLIC_SENTRY_ENVIRONMENT,
                release:
                    import.meta.env.VITE_PUBLIC_SENTRY_RELEASE || undefined,
                sendDefaultPii: true,
                enabled: analyticsEnabled,
                beforeSend: createClientBeforeSend(recoverFromDeploySkew),
                beforeSendTransaction: scrubContactTokens,
                beforeBreadcrumb: scrubContactTokens,
            });
        });
    }, 0);
}

if (typeof window !== "undefined") {
    installDeploySkewRecovery();

    if (
        import.meta.env.DEV &&
        ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
    ) {
        void import("react-scan").then(({ scan }) => {
            scan({ enabled: true });
        });
    }

    if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
    }

    bootSentry();
}

export const getRouter = () => {
    const queryClient = new QueryClient({
        defaultOptions: { hydrate: { queries: STREAMED_QUERY_RETRY } },
    });
    const dbClient = new DbClient({ queryClient });

    const router = createRouter({
        routeTree,
        context: { dbClient, queryClient },
        scrollRestoration: ({ location }) => location.pathname !== "/discover",
        getScrollRestorationKey,
        defaultPreload: shouldPreloadOnIntent() ? "intent" : false,
        defaultPreloadStaleTime: 0,
    });
    setupRouterSsrQueryIntegration({
        router,
        queryClient,
    });

    if (typeof window !== "undefined") {
        watchSessionViewer(setMatchSearchViewer);
        rememberMatchSearchFromLocation(router.state.location);
        router.subscribe("onResolved", (event) => {
            rememberMatchSearchFromLocation(event.toLocation);
            capturePageview(event.toLocation.pathname);
            if (document.body.style.pointerEvents === "none") {
                document.body.style.removeProperty("pointer-events");
            }
            document.documentElement.removeAttribute("data-search-sheet");
        });
    }

    return routerWithDbClient(router, dbClient);
};

declare module "@tanstack/react-router" {
    interface Register {
        router: ReturnType<typeof getRouter>;
    }

    interface HistoryState {
        searchCorrelation?: {
            runtimeId: string;
            context: SearchTerminalContext;
        };
    }
}
